/**
 * Gestionnaire de session sécurisé
 * - Stocke le token en mémoire (pas dans localStorage) pour réduire le risque XSS
 * - Valide l'expiration du JWT
 * - Les données non-sensibles restent dans localStorage pour la persistance
 */

let inMemoryToken = null;

/**
 * Décode un JWT sans vérifier la signature (côté client)
 */
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Vérifie si un token JWT est expiré
 */
function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return false; // Si pas d'exp, on ne peut pas vérifier
  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
}

/**
 * Stocke le token en mémoire et les infos utilisateur dans localStorage
 */
function saveSession({ uid, email, name, token, state, photoURL }) {
  inMemoryToken = token;
  // On garde les infos non-sensibles dans localStorage pour la persistance
  localStorage.setItem('email', email || '');
  localStorage.setItem('uid', uid || '');
  localStorage.setItem('name', name || '');
  localStorage.setItem('state', state || '');
  if (photoURL) localStorage.setItem('photoURL', photoURL);
  // Le token est aussi stocké dans localStorage comme fallback pour le rechargement de page
  // mais sera validé à chaque utilisation
  localStorage.setItem('token', token || '');
}

/**
 * Récupère le token valide (en mémoire ou localStorage)
 * Retourne null si le token est expiré
 */
function getToken() {
  const token = inMemoryToken || localStorage.getItem('token');
  if (!token) return null;
  if (isTokenExpired(token)) {
    clearSession();
    return null;
  }
  // Synchroniser en mémoire
  inMemoryToken = token;
  return token;
}

/**
 * Récupère les données de session depuis localStorage
 */
function getSessionData() {
  const token = getToken();
  if (!token) return null;
  return {
    uid: localStorage.getItem('uid'),
    email: localStorage.getItem('email'),
    name: localStorage.getItem('name'),
    photoURL: localStorage.getItem('photoURL'),
    state: localStorage.getItem('state'),
    token
  };
}

/**
 * Nettoie la session (déconnexion)
 */
function clearSession() {
  inMemoryToken = null;
  localStorage.removeItem('token');
  localStorage.removeItem('email');
  localStorage.removeItem('uid');
  localStorage.removeItem('name');
  localStorage.removeItem('state');
  localStorage.removeItem('photoURL');
}

/**
 * Vérifie si l'utilisateur a une session active et valide
 */
function hasValidSession() {
  return getToken() !== null;
}

export {
  saveSession,
  getToken,
  getSessionData,
  clearSession,
  hasValidSession,
  isTokenExpired,
  decodeJWT
};
